var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { StyleSheet, TouchableWithoutFeedback, ImageBackground } from 'react-native';
import { useVirtualRoom } from '../../../state/redux/selector/virtualRoom';
import { DNABox, Iffy, Stack, useTimeout, util, useOrientationChange, useWindowSize, useInterval, DNAChip, useVisualViewportSize, } from '@alucio/lux-ui';
import { useVideoContext, DeviceSettings, } from '../../../main';
import { AUDIO_SOURCE, CONTENT_MODE, virtualRoomActions } from '../../../state/redux/slice/virtualRoom';
import { DNAText } from '@alucio/lux-ui/src';
import useScreenShareParticipant from '../../../../src/hooks/useScreenShareParticipant/useScreenShareParticipant';
import DNAToolbar from '../Toolbar/DNAToolbar';
import ScreenShareTracks from '../../ParticipantTracks/ShareScreen';
import DNAParticipantData from '../DNAParticipantData/DNAParticipantData';
import { DNARecordingMobileBadge } from '../DNARecordingBadge/DNARecordingBadge';
import DNAInternationalNumbers from '../DNADialer/DNAInternationalNumbers';
import useLocalVideoToggle from '../../../../src/hooks/useLocalVideoToggle/useLocalVideoToggle';
import useLocalAudioToggle from '../../../../src/hooks/useLocalAudioToggle/useLocalAudioToggle';
import { Chat } from '../../Chat/Chat';
import DNAListParticipants from '../DNAListParticipants/DNAListParticipants';
import { DNAMenuItem } from '../DNAMenuItem/DNAMenuItem';
import { Logger } from '@aws-amplify/core';
const logger = new Logger('VIRTUAL', 'INFO');
const styles = StyleSheet.create({
    backgroundImage: {
        flex: 1,
    },
    container: {
        backgroundColor: '#0F0F0F',
        overflow: 'hidden',
    },
    videoContainerPortrait: {
        marginLeft: 59,
        marginRight: 59,
    },
    videoContainerLandscape: {
        marginLeft: 106,
        marginRight: 106,
    },
    welcomeText: {
        textAlign: 'center',
        paddingLeft: 5,
        paddingRight: 5,
    },
    menuOptionChip: {
        position: 'absolute',
        top: 0,
        right: 0,
        height: 16,
        fontSize: 11,
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: 20,
        paddingRight: 20,
    },
});
export default function DNARoom(props) {
    const { onLeaving, presentation } = props;
    const { localParticipant, contentMode, dialInNumbers, roomState, selectedAudioMode } = useVirtualRoom();
    const { connect, room, isConnecting, getLocalDataTrack, unreadMsgCount, chatVisible, toggleChatVis, } = useVideoContext();
    const [menuVisible, setMenuVisible] = useState(false);
    const [settingVisible, setSettingVisible] = useState(false);
    const [showDialerWindow, setShowDialerWindow] = useState(false);
    const [participantListVisible, setParticipantListVisible] = useState(false);
    const dispatch = useDispatch();
    const screenShareParticipant = useScreenShareParticipant();
    const size = useWindowSize();
    const orientation = useOrientationChange();
    const [isVideoEnabled, toggleVideoEnabled] = useLocalVideoToggle();
    const [isAudioEnabled, toggleAudioEnabled] = useLocalAudioToggle();
    const [isInitialState, setInitialState] = useState(true);
    const latestValidJoinToken = useRef('');
    const latestAttendeeState = useRef(null);
    const vvpSize = useVisualViewportSize();
    // We use a ref intead of state to allow handleDisconnect (which is bound to a twilio event) to be able to read
    // the latest value
    const isLeavingRef = React.useRef(false);
    useTimeout({
        timeout: 3000,
        onIdle: () => menuVisible && setMenuVisible(false),
    });
    useEffect(() => {
        function init() {
            return __awaiter(this, void 0, void 0, function* () {
                if (localParticipant && localParticipant.joinToken && roomState === 'disconnected' && !isConnecting) {
                    logger.debug('Connecting to Twilio');
                    // Instantiate the data track. Must be called before the connect.
                    getLocalDataTrack();
                    // Temporary disable video
                    isVideoEnabled && toggleVideoEnabled();
                    isAudioEnabled && toggleAudioEnabled();
                    if (yield connect(localParticipant.joinToken)) {
                        latestValidJoinToken.current = localParticipant.joinToken;
                        logger.debug('Connected to Twilio');
                    }
                    else {
                        props.onError('Unable to connect to the meeting', true);
                    }
                }
            });
        }
        init();
    }, [localParticipant]);
    useEffect(() => {
        latestAttendeeState.current = Object.assign({}, props.attendee);
        function reconnect() {
            return __awaiter(this, void 0, void 0, function* () {
                logger.info('Attempting to re-join room');
                // Instantiate the data track. Must be called before the connect.
                getLocalDataTrack();
                // Temporary disable video
                isVideoEnabled && toggleVideoEnabled();
                isAudioEnabled && toggleAudioEnabled();
                // There's a strange state when reconnecting that whatever is being presented doesn't seem to be in sync.
                // To solve this, we remove any share state and we'll update it when receiving the heartbeat from the host.
                dispatch(virtualRoomActions.setContentMode(CONTENT_MODE.NONE));
                if (yield connect(localParticipant.joinToken)) {
                    logger.info('Re-Joined to Room');
                    latestValidJoinToken.current = localParticipant.joinToken;
                }
                else {
                    props.onError('Unable to connect to the meeting', true);
                }
            });
        }
        if (room.state === 'disconnected' && !isLeavingRef.current) {
            logger.info('Room Disconnected');
            dispatch(virtualRoomActions.onRoomReJoining());
            // The reason of this timeout is because we need to tell apart the user from being disconnected and being removed by host.
            // Since, when removed, the room's listener of the disconnection occurs first than our subscription of the attendee's status, we want to wait
            // for that record to be updated in redux before trying reconnecting. If, after 3 seconds, the attendee's record remains still, we can try reconneting again.
            setTimeout(() => {
                if (latestAttendeeState.current.attendeeId && latestAttendeeState.current.status === 'CONNECTED') {
                    reconnect();
                }
            }, 1500);
        }
    }, [room.state, props.attendee]);
    useEffect(() => {
        if (room.state === 'connected') {
            logger.debug('Room now in connected state');
            const handleParticipantConnected = (e) => {
                dispatch(virtualRoomActions.participantConnected(e));
            };
            const handleParticipantDisconnected = (e) => {
                dispatch(virtualRoomActions.participantDisconnected(e.identity));
            };
            dispatch(virtualRoomActions.onRoomConnected({ localParticipantIdentity: room.localParticipant.identity }));
            // Add all existing participants
            room.participants.forEach((p) => {
                dispatch(virtualRoomActions.participantConnected(p));
            });
            const handleRoomDisconnected = () => {
                // When the room is finished
                handleDisconnect();
            };
            const handleRoomReconnected = () => {
                logger.info('Twilio is reconnected');
            };
            const handleRoomReconnecting = (error) => {
                logger.info('Reconnecting to room', error);
            };
            room.on('participantDisconnected', handleParticipantDisconnected);
            room.on('participantConnected', handleParticipantConnected);
            room.on('disconnected', handleRoomDisconnected);
            room.on('reconnecting', handleRoomReconnecting);
            room.on('reconnected', handleRoomReconnected);
            return () => {
                room
                    .off('participantDisconnected', handleParticipantDisconnected)
                    .off('participantConnected', handleParticipantConnected)
                    .off('disconnected', handleRoomDisconnected)
                    .off('reconnecting', handleRoomReconnecting)
                    .off('reconnected', handleRoomReconnected);
            };
        }
    }, [room]);
    const handleDisconnect = () => {
        logger.debug('Entered handleDisconnect');
        try {
            const videoTracks = [...room.localParticipant.videoTracks.values()];
            const audioTracks = [...room.localParticipant.audioTracks.values()];
            videoTracks.forEach(trackPub => {
                trackPub.unpublish();
                trackPub.track.stop();
                const mediaElements = trackPub.track.detach();
                mediaElements.forEach(mediaElement => mediaElement.remove());
            });
            audioTracks.forEach(trackPub => {
                trackPub.unpublish();
                trackPub.track.stop();
                const mediaElements = trackPub.track.detach();
                mediaElements.forEach(mediaElement => mediaElement.remove());
            });
        }
        catch (e) {
            throw new Error(`Error ending the call ${JSON.stringify(e)}`);
        }
        if (isLeavingRef.current) {
            props.onDisconnect();
        }
        else {
            logger.debug('User unintentionally disconnected. Not firing event');
        }
    };
    const handleEndCall = () => {
        isLeavingRef.current = true;
        onLeaving();
    };
    // Temporary set content mode
    useEffect(() => {
        if (screenShareParticipant) {
            dispatch(virtualRoomActions.setContentMode(CONTENT_MODE.SCREEN_SHARE));
            setInitialState(false);
        }
        else if (contentMode !== CONTENT_MODE.CONTENT_SHARE) {
            dispatch(virtualRoomActions.setContentMode(CONTENT_MODE.NONE));
        }
        else {
            setInitialState(false);
        }
    }, [screenShareParticipant, contentMode]);
    useInterval(() => __awaiter(this, void 0, void 0, function* () {
        if (room.state === 'connected') {
            const stats = room.localParticipant.networkQualityStats;
            if (stats) {
                dispatch(virtualRoomActions.updateCallStats(stats));
            }
        }
    }), 10000);
    useEffect(() => {
        setShowDialerWindow(selectedAudioMode === AUDIO_SOURCE.PHONE && !localParticipant.phoneIdentity);
    }, [selectedAudioMode, localParticipant.phoneIdentity]);
    useEffect(() => {
        // This scroll was not always working until everything was rendered
        window.setTimeout(() => { window.scrollTo(1, 1); }, 100);
    }, [orientation]);
    const fillWindowStyle = {
        height: (vvpSize === null || vvpSize === void 0 ? void 0 : vvpSize.height) || size.height,
        width: size.width,
        overflow: 'hidden',
    };
    return (React.createElement(TouchableWithoutFeedback, { onPress: () => setMenuVisible(!menuVisible) },
        React.createElement(DNABox, { appearance: "col", style: [styles.container, fillWindowStyle] },
            React.createElement(Iffy, { is: isConnecting },
                React.createElement(ImageBackground, { source: props.background, style: { flex: 1, backgroundColor: 'black' }, imageStyle: { opacity: 0.5 } },
                    React.createElement(DNABox, { alignX: "center", fit: "fill", alignY: "center" },
                        React.createElement(DNAText, { h2: true, status: "basic" }, "Connecting.....")))),
            React.createElement(Iffy, { is: roomState === 'connected' },
                React.createElement(DNAParticipantData, null),
                React.createElement(DNARecordingMobileBadge, null),
                React.createElement(Iffy, { is: chatVisible },
                    React.createElement(Chat, null)),
                React.createElement(Stack, { anchor: "bottomLeft", style: fillWindowStyle },
                    React.createElement(Stack.Layer, { style: fillWindowStyle },
                        React.createElement(Iffy, { is: contentMode === CONTENT_MODE.NONE },
                            React.createElement(ImageBackground, { source: props.background, style: { flex: 1, backgroundColor: 'black' }, imageStyle: { opacity: 0.5 } },
                                React.createElement(Iffy, { is: isInitialState },
                                    React.createElement(DNABox, { fill: true, alignX: "center", alignY: "center" },
                                        React.createElement(DNAText, { h1: true, status: "basic", style: styles.welcomeText }, "Welcome to the meeting!"))))),
                        React.createElement(Iffy, { is: contentMode === CONTENT_MODE.SCREEN_SHARE },
                            React.createElement(DNABox, { appearance: orientation === 'PORTRAIT' ? 'row' : 'col', fit: "fill" },
                                React.createElement(ScreenShareTracks
                                // @ts-expect-error -> type script doesnt know that iffy prevents the null
                                , { 
                                    // @ts-expect-error -> type script doesnt know that iffy prevents the null
                                    participant: screenShareParticipant, onStopShare: () => { } }))),
                        React.createElement(Iffy, { is: contentMode === CONTENT_MODE.CONTENT_SHARE },
                            React.createElement(DNABox, { appearance: orientation === 'PORTRAIT' ? 'row' : 'col', fit: "fill" }, presentation))),
                    React.createElement(Stack.Layer, { style: { width: '100%' } },
                        React.createElement(Iffy, { is: !menuVisible && unreadMsgCount },
                            React.createElement(DNAMenuItem, { labels: ['Chat', 'Chat'], enabled: true, icons: ['chat-processing', 'chat-processing'], onPress: toggleChatVis, chip: React.createElement(Iffy, { is: unreadMsgCount > 0 },
                                    React.createElement(DNAChip, { style: styles.menuOptionChip, appearance: "tag", status: "danger" }, unreadMsgCount.toString())) })),
                        React.createElement(Iffy, { is: menuVisible },
                            React.createElement(DNAToolbar, { onEndPress: handleEndCall, onSettingPress: () => setSettingVisible(!settingVisible), onPersonListPress: () => (setParticipantListVisible(!participantListVisible)) }))),
                    React.createElement(Stack.Layer, { style: util.mergeStyles(undefined, size, [{ pointerEvents: 'none' }, !settingVisible]) },
                        React.createElement(Iffy, { is: settingVisible },
                            React.createElement(DNABox, { appearance: "row", fit: "fill", alignX: "center", alignY: "center" },
                                React.createElement(DeviceSettings, { onDone: () => setSettingVisible(false), audioFiles: [], disableLocalAudio: selectedAudioMode === AUDIO_SOURCE.PHONE, mobileView: true })))),
                    React.createElement(Stack.Layer, null,
                        React.createElement(Iffy, { is: participantListVisible },
                            React.createElement(DNABox, { appearance: "row", fit: "fill", alignX: "stretch", alignY: "stretch" },
                                React.createElement(DNAListParticipants, { onClose: () => setParticipantListVisible(false) })))),
                    React.createElement(Stack.Layer, { style: util.mergeStyles(undefined, size, [{ pointerEvents: 'none' }, !showDialerWindow]) },
                        React.createElement(Iffy, { is: showDialerWindow },
                            React.createElement(DNABox, { appearance: "row", fit: "fill", alignX: "center", alignY: "center" },
                                React.createElement(DNAInternationalNumbers, { dialInNumbers: dialInNumbers, accessCode: localParticipant.phonePin, onClose: () => {
                                        setShowDialerWindow(false);
                                    } })))))))));
}
//# sourceMappingURL=DNARoom.js.map